import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Clock, Tag, ChevronRight } from "lucide-react";
import ContactForm from "../components/ContactForm";
import { PRODUCTS } from "../data/products";
import { BLOGS } from "../data/blogs";
import { COOKBOOKS } from "../data/cookbooks";

const CATEGORY_COLORS = {
  "memory": "#15803D",
  "evals": "#7E22CE",
  "mcp-tools": "#BE185D",
};

// ─── Endless-runner game constants ───────────────────────────────
const PLAYER_X_PCT = 0.18;       // player horizontal position (% of hero width)
const BASE_SPEED = 2.5;          // starting scroll speed (px/frame)
const MAX_SPEED = 8;             // cap
const SPEED_RAMP = 0.0018;       // per-frame acceleration
const GRAVITY = 0.85;
const JUMP_VELOCITY = 16;
const FALL_DEATH = -80;          // y < this = dead
const LEVEL_DISTANCE = 1200;     // px traveled per level

// Building visual recipe pool (px). For both idle and game.
const BUILDING_RECIPE = [
  { w: 84, h: 240 }, { w: 64, h: 280 }, { w: 56, h: 200 }, { w: 96, h: 320 },
  { w: 72, h: 260 }, { w: 80, h: 220 }, { w: 64, h: 290 }, { w: 88, h: 250 },
  { w: 56, h: 300 }, { w: 76, h: 230 }, { w: 60, h: 270 }, { w: 92, h: 320 },
];

let buildingIdSeq = 0;
function nextBuildingId() { return ++buildingIdSeq; }

function makeWindows(w, h) {
  const cols = Math.max(1, Math.floor((w - 16) / 14));
  const rows = Math.max(1, Math.floor((h - 30) / 18));
  const out = [];
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      if (Math.random() < 0.45) {
        out.push({ x: 8 + c * 14, y: 14 + r * 18 });
      }
    }
  }
  return out;
}

function makeBuilding(x, recipeIndex) {
  const r = BUILDING_RECIPE[recipeIndex % BUILDING_RECIPE.length];
  return { id: nextBuildingId(), x, w: r.w, h: r.h, windows: makeWindows(r.w, r.h) };
}

// First N buildings are continuous (no gaps) so the player gets a runway.
const SAFE_RUN_LENGTH = 30;

function generateInitialBuildings(viewportWidth) {
  const out = [];
  // First building: extra-wide to guarantee the spawn point sits on it
  const safeWidth = Math.max(260, Math.ceil(viewportWidth * (PLAYER_X_PCT + 0.18)));
  out.push({
    id: nextBuildingId(),
    x: 0,
    w: safeWidth,
    h: 240,
    windows: makeWindows(safeWidth, 240),
  });
  let x = safeWidth;
  let i = 1;
  while (x < viewportWidth + 1200) {
    const b = makeBuilding(x, i);
    out.push(b);
    const gap = i < SAFE_RUN_LENGTH ? 0 : 18 + Math.floor(Math.random() * 56);
    x = b.x + b.w + gap;
    i++;
  }
  return out;
}

export default function Home() {
  const products = PRODUCTS.slice(0, 3);
  const blogs = BLOGS.slice(0, 3);
  const cookbooks = COOKBOOKS.slice(0, 3);
  const DIFFICULTY_COLORS = { Beginner: "#15803D", Intermediate: "#BE185D", Advanced: "#7E22CE" };
  const [booting, setBooting] = useState(true);

  // Game state
  const [gameActive, setGameActive] = useState(false);
  const [gameOver, setGameOver] = useState(false);
  const [score, setScore] = useState(0);
  const [highScore, setHighScore] = useState(() => {
    if (typeof window === "undefined") return 0;
    const stored = window.localStorage.getItem("melonai.highScore");
    return stored ? parseInt(stored, 10) || 0 : 0;
  });
  const [isNewHighScore, setIsNewHighScore] = useState(false);
  const heroRef = useRef(null);
  const heroWidthRef = useRef(1200);
  const buildingsRef = useRef([]);
  const playerRef = useRef({ y: 240, vy: 0, canJump: true });
  const speedRef = useRef(BASE_SPEED);
  const distRef = useRef(0);
  const spawnedRef = useRef(0); // # buildings spawned this run, for gap policy
  const [, forceTick] = useState(0);

  useEffect(() => {
    const timer = setTimeout(() => setBooting(false), 800);
    return () => clearTimeout(timer);
  }, []);

  // Track hero width
  useEffect(() => {
    if (!heroRef.current) return;
    const measure = () => {
      heroWidthRef.current = heroRef.current?.clientWidth || 1200;
      if (buildingsRef.current.length === 0) {
        buildingsRef.current = generateInitialBuildings(heroWidthRef.current);
        forceTick((n) => n + 1);
      }
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(heroRef.current);
    return () => ro.disconnect();
  }, []);

  // ─── Mario / endless-runner ────────────────────────────────────
  const placePlayerOnSpawn = () => {
    const px = heroWidthRef.current * PLAYER_X_PCT;
    const start = buildingsRef.current.find((b) => px >= b.x && px <= b.x + b.w);
    playerRef.current = { y: start ? start.h : 240, vy: 0, canJump: true };
  };
  const startGame = (e) => {
    if (gameActive) return;
    if (e?.target?.closest("a, button")) return;
    buildingsRef.current = generateInitialBuildings(heroWidthRef.current);
    placePlayerOnSpawn();
    speedRef.current = BASE_SPEED;
    distRef.current = 0;
    spawnedRef.current = buildingsRef.current.length;
    setScore(0);
    setGameOver(false);
    setGameActive(true);
  };
  const restartGame = () => {
    buildingsRef.current = generateInitialBuildings(heroWidthRef.current);
    placePlayerOnSpawn();
    speedRef.current = BASE_SPEED;
    distRef.current = 0;
    spawnedRef.current = buildingsRef.current.length;
    setScore(0);
    setGameOver(false);
  };
  const exitGame = () => {
    setGameActive(false);
    setGameOver(false);
    // Reset buildings to a fresh static set (positions reset)
    buildingsRef.current = generateInitialBuildings(heroWidthRef.current);
    forceTick((n) => n + 1);
  };

  // Keyboard
  useEffect(() => {
    if (!gameActive) return;
    const isJumpKey = (c) => c === "Space" || c === "ArrowUp" || c === "KeyW" || c === "ArrowRight";
    const onDown = (e) => {
      if (e.code === "Escape") { exitGame(); return; }
      if (e.code === "KeyR" && gameOver) { restartGame(); return; }
      if (gameOver) return;
      if (isJumpKey(e.code)) {
        e.preventDefault();
        if (playerRef.current.canJump) {
          playerRef.current.vy = JUMP_VELOCITY;
          playerRef.current.canJump = false;
        }
      }
    };
    window.addEventListener("keydown", onDown);
    return () => window.removeEventListener("keydown", onDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameActive, gameOver]);

  // Click-to-jump while game is active
  const handleHeroClick = (e) => {
    if (!gameActive) {
      startGame(e);
      return;
    }
    if (gameOver) return;
    if (e.target.closest("a, button")) return;
    if (playerRef.current.canJump) {
      playerRef.current.vy = JUMP_VELOCITY;
      playerRef.current.canJump = false;
    }
  };

  // Game loop
  useEffect(() => {
    if (!gameActive || gameOver) return;
    let raf;
    const tick = () => {
      const p = playerRef.current;
      const buildings = buildingsRef.current;
      const heroW = heroWidthRef.current;
      const playerScreenX = heroW * PLAYER_X_PCT;

      // Scroll buildings left
      const speed = speedRef.current;
      for (const b of buildings) b.x -= speed;
      // Cull off-screen buildings, spawn new ones
      while (buildings.length && buildings[0].x + buildings[0].w < -50) {
        buildings.shift();
      }
      const last = buildings[buildings.length - 1];
      if (!last || last.x + last.w < heroW + 200) {
        // Level-based difficulty: wider gaps the further you get
        const level = Math.floor(distRef.current / LEVEL_DISTANCE) + 1;
        const tier = Math.max(0, level - 2); // level 1 = no gap tier, level 2+ starts scaling
        const minGap = Math.min(90, 20 + tier * 14);
        const maxGap = Math.min(150, 55 + tier * 22);
        const gap =
          spawnedRef.current < SAFE_RUN_LENGTH || level < 2
            ? 0
            : minGap + Math.floor(Math.random() * (maxGap - minGap + 1));
        const startX = last ? last.x + last.w + gap : heroW;
        buildings.push(makeBuilding(startX, spawnedRef.current));
        spawnedRef.current += 1;
      }

      // Physics
      const prevY = p.y;
      p.vy -= GRAVITY;
      p.y += p.vy;

      // Find building under player (player center)
      const under = buildings.find(
        (b) => playerScreenX >= b.x && playerScreenX <= b.x + b.w
      );

      // Land on top of building only when falling AND previously above its top
      if (under && p.vy <= 0 && prevY >= under.h && p.y <= under.h) {
        p.y = under.h;
        p.vy = 0;
        p.canJump = true;
      } else if (!under) {
        p.canJump = false;
      }

      // Death
      if (p.y < FALL_DEATH) {
        const finalScore = Math.floor(distRef.current / 10);
        setScore(finalScore);
        if (finalScore > highScore) {
          setHighScore(finalScore);
          setIsNewHighScore(true);
          try { window.localStorage.setItem("melonai.highScore", String(finalScore)); } catch {}
        } else {
          setIsNewHighScore(false);
        }
        setGameOver(true);
        return;
      }

      // Score: distance traveled
      distRef.current += speed;
      speedRef.current = Math.min(MAX_SPEED, speed + SPEED_RAMP);

      if ((distRef.current | 0) % 10 === 0) {
        setScore(Math.floor(distRef.current / 10));
      }

      forceTick((n) => n + 1);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameActive, gameOver]);

  return (
    <div data-testid="home-page" className="bg-[#F5F5F0]">
      {/* ── HERO (sky + scrolling pixel-city endless runner) ─────── */}
      <section
        ref={heroRef}
        data-testid="hero-section"
        onClick={handleHeroClick}
        className={`relative min-h-screen flex flex-col justify-end overflow-hidden ${gameActive && !gameOver ? "cursor-pointer" : "cursor-pointer"}`}
        style={{
          backgroundImage: "url(/images/melonai/hero.svg)",
          backgroundSize: "cover",
          backgroundPosition: "center top",
        }}
      >
        {/* Buildings - deep green, tinted by the bottom-half cream wash */}
        <div className="absolute inset-x-0 bottom-0 h-[55%] z-[2] pointer-events-none overflow-hidden">
          {buildingsRef.current.map((b) => (
            <div
              key={b.id}
              className="absolute bottom-0 bg-[#15803D]"
              style={{
                left: `${b.x}px`,
                width: `${b.w}px`,
                height: `${b.h}px`,
                boxShadow: "inset 0 -3px 0 0 #14532D",
              }}
            >
              {b.windows.map((w, i) => (
                <span
                  key={i}
                  className="absolute bg-[#FCD34D]"
                  style={{ left: `${w.x}px`, top: `${w.y}px`, width: 4, height: 4 }}
                />
              ))}
            </div>
          ))}
        </div>

        {/* Cream wash - bottom half only (over buildings + mountains). Sky/sun/clouds/birds stay vivid. */}
        <div
          className={`absolute inset-x-0 bottom-0 z-[3] pointer-events-none transition-colors duration-300 ${gameActive ? "bg-[#F5F5F0]/35" : "bg-[#F5F5F0]/65"}`}
          style={{ height: "58%" }}
        />

        {/* Birds - detailed side-profile, full saturation, bigger than before */}
        <img
          src="/images/melonai/bird.svg"
          alt=""
          aria-hidden="true"
          className="bird-fly absolute top-[38%] left-0 w-20 h-10 pointer-events-none select-none z-[5] [image-rendering:pixelated]"
        />
        <img
          src="/images/melonai/bird.svg"
          alt=""
          aria-hidden="true"
          className="bird-fly absolute top-[30%] left-0 w-14 h-7 pointer-events-none select-none z-[5] [image-rendering:pixelated]"
          style={{ animationDelay: "-8s", animationDuration: "26s" }}
        />
        <img
          src="/images/melonai/bird.svg"
          alt=""
          aria-hidden="true"
          className="bird-fly absolute top-[48%] left-0 w-16 h-8 pointer-events-none select-none z-[5] [image-rendering:pixelated]"
          style={{ animationDelay: "-15s", animationDuration: "20s" }}
        />
        <img
          src="/images/melonai/bird.svg"
          alt=""
          aria-hidden="true"
          className="bird-fly absolute top-[26%] left-0 w-12 h-6 pointer-events-none select-none z-[5] [image-rendering:pixelated]"
          style={{ animationDelay: "-3s", animationDuration: "32s" }}
        />

        {/* Hint to start the game (idle only) */}
        {!gameActive && (
          <div className="absolute top-24 right-6 md:right-10 z-[6] font-arcade text-xs md:text-sm text-[#15803D] uppercase tracking-widest pointer-events-none animate-blink">
            ▶ Click anywhere to play
          </div>
        )}

        {/* Hero text content - only when NOT in game */}
        {!gameActive && (
          <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 pb-20 w-full">
            <p className="font-arcade text-[#15803D] text-sm md:text-base mb-6">
              {booting ? "> INITIATING MELONAI.SYS..." : "> MELONAI.SYS READY_"}
            </p>

            <h1
              data-testid="hero-title"
              className="font-pixel text-3xl md:text-5xl lg:text-6xl text-[#0A0A0A] leading-none tracking-tighter mb-2 animate-fadeInUp opacity-0 [animation-fill-mode:forwards]"
              style={{ animationDelay: "200ms" }}
            >
              MELON<span className="text-[#15803D] animate-glitch-loop inline-block">AI</span>
            </h1>

            <div
              className="w-20 h-1 bg-[#15803D] mb-5 animate-fadeInUp opacity-0 [animation-fill-mode:forwards]"
              style={{ animationDelay: "350ms" }}
            />

            <p
              className="font-pixel text-sm md:text-lg text-[#0A0A0A]/85 mb-1 animate-fadeInUp opacity-0 [animation-fill-mode:forwards]"
              style={{ animationDelay: "450ms" }}
            >
              WE BUILD THE BRAINS.
            </p>
            <p
              className="font-pixel text-sm md:text-lg text-[#15803D] mb-8 animate-fadeInUp opacity-0 [animation-fill-mode:forwards]"
              style={{ animationDelay: "550ms" }}
            >
              YOU BUILD THE BODY.
            </p>

            <div
              className="flex flex-col sm:flex-row gap-4 animate-fadeInUp opacity-0 [animation-fill-mode:forwards]"
              style={{ animationDelay: "700ms" }}
            >
              <Link
                to="/products"
                data-testid="hero-cta-products"
                className="inline-flex items-center gap-3 bg-[#15803D] text-white font-arcade text-lg uppercase tracking-widest px-7 py-3 border-2 border-[#15803D] hover:bg-transparent hover:text-[#15803D] hover:shadow-[4px_4px_0px_0px_rgba(21,128,61,0.4)] transition-colors duration-150 btn-press"
              >
                EXPLORE PRODUCTS <ArrowRight size={16} />
              </Link>
              <a
                href={`mailto:${process.env.REACT_APP_EMAIL || "vikram.dev@alumni.iitd.ac.in"}`}
                data-testid="hero-cta-contact"
                className="inline-flex items-center gap-3 bg-transparent text-[#15803D] font-arcade text-lg uppercase tracking-widest px-7 py-3 border-2 border-[#15803D] hover:bg-[#15803D] hover:text-white transition-colors duration-150 btn-press"
              >
                GET IN TOUCH
              </a>
            </div>
          </div>
        )}

        {/* ── Game-mode overlays ────────────────────────────────── */}
        {gameActive && (
          <>
            {/* Player */}
            <img
              src="/images/melonai/mel-char.svg"
              alt=""
              aria-hidden="true"
              className="absolute z-[8] pointer-events-none select-none [image-rendering:pixelated]"
              style={{
                left: `${PLAYER_X_PCT * 100}%`,
                bottom: `${playerRef.current.y}px`,
                width: "36px",
                height: "44px",
                transform: "translate(-50%, 0)",
                transition: "none",
              }}
            />

            {/* HUD */}
            <div className="absolute top-20 left-1/2 -translate-x-1/2 z-[9] font-arcade text-sm md:text-base text-[#0A0A0A] bg-white/90 border-2 border-[#15803D] px-4 py-2 shadow-pixel-sm flex items-center gap-4">
              <span>SPACE / TAP - JUMP</span>
              <span className="text-[#15803D]">·</span>
              <span>ESC EXIT</span>
              <span className="text-[#15803D]">·</span>
              <span className="font-pixel text-[#15803D]">LVL {Math.floor(distRef.current / LEVEL_DISTANCE) + 1}</span>
              <span className="text-[#15803D]">·</span>
              <span className="font-pixel text-[#15803D]">SCORE {score}</span>
              <span className="text-[#15803D]">·</span>
              <span className="font-pixel text-[#BE185D]">BEST {highScore}</span>
            </div>

            {/* Game over */}
            {gameOver && (
              <div
                className="absolute inset-0 z-[20] flex items-center justify-center bg-[#0A0A0A]/70"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="bg-white border-4 border-[#15803D] p-8 md:p-10 text-center shadow-pixel max-w-sm">
                  <p className="font-pixel text-2xl md:text-3xl text-[#BE185D] mb-3">GAME OVER</p>
                  {isNewHighScore ? (
                    <p className="font-arcade text-base text-[#15803D] mb-2 uppercase tracking-wider animate-pulse">
                      ★ New High Score ★
                    </p>
                  ) : (
                    <p className="font-arcade text-base text-[#0A0A0A]/75 mb-2 uppercase tracking-wider">
                      You fell in the gap.
                    </p>
                  )}
                  <p className="font-pixel text-lg text-[#15803D] mb-1">LVL {Math.floor(distRef.current / LEVEL_DISTANCE) + 1}</p>
                  <p className="font-pixel text-lg text-[#15803D] mb-1">SCORE {score}</p>
                  <p className="font-pixel text-base text-[#BE185D] mb-6">BEST {highScore}</p>
                  <div className="flex gap-3 justify-center">
                    <button
                      onClick={(e) => { e.stopPropagation(); restartGame(); }}
                      className="bg-[#15803D] text-white font-arcade text-base uppercase tracking-widest px-5 py-3 border-2 border-[#15803D] hover:bg-transparent hover:text-[#15803D] transition-colors duration-150 btn-press"
                    >
                      Restart [R]
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); exitGame(); }}
                      className="bg-transparent text-[#15803D] font-arcade text-base uppercase tracking-widest px-5 py-3 border-2 border-[#15803D] hover:bg-[#15803D] hover:text-white transition-colors duration-150 btn-press"
                    >
                      Exit [Esc]
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </section>

      {/* ── PRODUCTS ─────────────────────────────────────────────── */}
      <section data-testid="products-section" className="py-24 px-4 md:px-8 max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <div>
            <p className="section-tag mb-3">&gt; PRODUCTS.EXE</p>
            <h2 className="font-pixel text-2xl md:text-4xl text-[#0A0A0A] leading-tight">
              OUR AI ARSENAL
            </h2>
          </div>
          <Link
            to="/products"
            data-testid="products-view-all"
            className="inline-flex items-center gap-2 font-arcade text-xl text-[#15803D] hover:underline uppercase"
          >
            VIEW ALL <ChevronRight size={16} />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {products.map((product, i) => (
            <Link
              key={product.id}
              to={`/products#${product.id}`}
              data-testid={`product-card-${i}`}
              className="group bg-white border-2 border-[#D4D4D4] card-hover overflow-hidden flex flex-col h-full"
              style={{ animationDelay: `${i * 100}ms` }}
            >
              <div className="relative h-48 overflow-hidden bg-[#F5F5F0] border-b-2 border-[#15803D]/15 flex items-center justify-center">
                <img
                  src={product.image_url}
                  alt={product.display_name}
                  className="w-full h-full object-cover [image-rendering:pixelated]"
                />
                <div className="absolute top-3 left-3">
                  <span
                    className="font-arcade text-base uppercase tracking-wide px-2 py-1 border bg-white/85"
                    style={{
                      color: CATEGORY_COLORS[product.category] || "#15803D",
                      borderColor: CATEGORY_COLORS[product.category] || "#15803D",
                    }}
                  >
                    [{product.category.replace("-", " ")}]
                  </span>
                </div>
                {product.status === "coming-soon" && (
                  <div className="absolute top-3 right-3 bg-white/90 border border-[#BE185D] px-2 py-1">
                    <span className="font-arcade text-xs text-[#BE185D] uppercase">SOON</span>
                  </div>
                )}
              </div>
              <div className="p-5 flex flex-col flex-1">
                <h3 className="font-pixel text-sm text-[#0A0A0A] mb-1">{product.display_name}</h3>
                <p className="font-arcade text-lg text-[#15803D] mb-3">{product.tagline}</p>
                <p className="font-terminal text-xs text-[#0A0A0A]/65 leading-relaxed mb-4 line-clamp-2">
                  {product.description}
                </p>
                <ul className="space-y-1 mb-5">
                  {product.features.slice(0, 3).map((f, fi) => (
                    <li key={fi} className="font-terminal text-xs text-[#0A0A0A]/70 flex items-start gap-2">
                      <span className="text-[#15803D] mt-0.5">&gt;</span> {f}
                    </li>
                  ))}
                </ul>
                <span className="inline-flex items-center gap-2 font-arcade text-base text-[#15803D] uppercase group-hover:underline mt-auto">
                  LEARN MORE <ArrowRight size={14} />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* ── BLOGS ────────────────────────────────────────────────── */}
      <section data-testid="blogs-section" className="py-24 px-4 md:px-8 max-w-7xl mx-auto border-t border-[#E5E5E5]">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <div>
            <p className="section-tag mb-3">&gt; LATEST_DISPATCHES</p>
            <h2 className="font-pixel text-2xl md:text-4xl text-[#0A0A0A] leading-tight">
              FROM THE LAB
            </h2>
          </div>
          <Link
            to="/blogs"
            data-testid="blogs-view-all"
            className="inline-flex items-center gap-2 font-arcade text-xl text-[#15803D] hover:underline uppercase"
          >
            VIEW ALL BLOGS <ChevronRight size={16} />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {blogs.map((blog) => (
            <Link
              key={blog.id}
              to={`/blogs/${blog.slug}`}
              data-testid={`blog-card-${blog.id}`}
              className="group bg-white border-2 border-[#D4D4D4] card-hover overflow-hidden flex flex-col h-full"
            >
              <div className="relative h-44 overflow-hidden bg-[#F5F5F0] border-b-2 border-[#7E22CE]/15 flex items-center justify-center">
                <img
                  src={blog.image_url}
                  alt={blog.title}
                  className="w-full h-full object-cover [image-rendering:pixelated]"
                />
                <div className="absolute top-3 left-3">
                  <span className="font-arcade text-base text-[#7E22CE] border border-[#7E22CE] bg-white/85 px-2 py-1 uppercase">
                    {blog.category}
                  </span>
                </div>
                {blog.status === "coming-soon" && (
                  <div className="absolute top-3 right-3 bg-white/90 border border-[#BE185D] px-2 py-1">
                    <span className="font-arcade text-xs text-[#BE185D] uppercase">SOON</span>
                  </div>
                )}
              </div>
              <div className="p-5 flex flex-col flex-1">
                <div className="flex items-center gap-3 mb-3">
                  <Clock size={12} className="text-[#0A0A0A]/45" />
                  <span className="font-terminal text-xs text-[#0A0A0A]/45">{blog.read_time} MIN READ</span>
                </div>
                <h3 className="font-pixel text-xs text-[#0A0A0A] leading-snug mb-3 flex-1">
                  {blog.title}
                </h3>
                <p className="font-terminal text-xs text-[#0A0A0A]/65 leading-relaxed mb-4 line-clamp-2">
                  {blog.excerpt}
                </p>
                <div className="flex flex-wrap gap-2 mb-4">
                  {blog.tags.slice(0, 2).map((tag) => (
                    <span key={tag} className="tag-chip flex items-center gap-1">
                      <Tag size={10} /> {tag}
                    </span>
                  ))}
                </div>
                <span className="font-arcade text-sm text-[#15803D] group-hover:underline uppercase mt-auto">
                  READ &rarr;
                </span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* ── COOKBOOKS ────────────────────────────────────────────── */}
      <section data-testid="cookbooks-section" className="py-24 px-4 md:px-8 max-w-7xl mx-auto border-t border-[#E5E5E5]">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <div>
            <p className="section-tag mb-3">&gt; COOKBOOKS.SYS</p>
            <h2 className="font-pixel text-2xl md:text-4xl text-[#0A0A0A] leading-tight">
              BUILD GUIDES
            </h2>
          </div>
          <Link
            to="/cookbooks"
            data-testid="cookbooks-view-all"
            className="inline-flex items-center gap-2 font-arcade text-xl text-[#15803D] hover:underline uppercase"
          >
            VIEW ALL COOKBOOKS <ChevronRight size={16} />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {cookbooks.map((book) => (
            <Link
              key={book.id}
              to={`/cookbooks/${book.slug}`}
              data-testid={`cookbook-card-${book.id}`}
              className="group bg-white border-2 border-[#D4D4D4] card-hover overflow-hidden flex flex-col h-full"
            >
              <div className="relative h-44 overflow-hidden bg-[#F5F5F0] border-b-2 border-[#B45309]/15 flex items-center justify-center">
                <img
                  src={book.image_url}
                  alt={book.title}
                  className="w-full h-full object-cover [image-rendering:pixelated]"
                />
                <div className="absolute top-3 left-3 flex gap-2">
                  <span
                    className="font-arcade text-base uppercase border px-2 py-1 bg-white/85"
                    style={{
                      color: DIFFICULTY_COLORS[book.difficulty] || "#15803D",
                      borderColor: DIFFICULTY_COLORS[book.difficulty] || "#15803D",
                    }}
                  >
                    {book.difficulty}
                  </span>
                </div>
                {book.status === "coming-soon" && (
                  <div className="absolute top-3 right-3 bg-white/90 border border-[#BE185D] px-2 py-1">
                    <span className="font-arcade text-xs text-[#BE185D] uppercase">SOON</span>
                  </div>
                )}
              </div>
              <div className="p-5 flex flex-col flex-1">
                <div className="flex items-center flex-wrap gap-2 mb-3">
                  <Clock size={12} className="text-[#0A0A0A]/45" />
                  <span className="font-terminal text-xs text-[#0A0A0A]/45">{book.read_time} MIN</span>
                  <span className="text-[#D4D4D4]">|</span>
                  <span className="font-terminal text-xs text-[#0A0A0A]/45 uppercase">{book.category}</span>
                </div>
                <h3 className="font-pixel text-xs text-[#0A0A0A] leading-snug mb-3 flex-1">
                  {book.title}
                </h3>
                <p className="font-terminal text-xs text-[#0A0A0A]/65 leading-relaxed mb-4 line-clamp-2">
                  {book.description}
                </p>
                <div className="flex flex-wrap gap-2 mb-4">
                  {book.tags.slice(0, 2).map((tag) => (
                    <span key={tag} className="tag-chip flex items-center gap-1">
                      <Tag size={10} /> {tag}
                    </span>
                  ))}
                </div>
                <span className="font-arcade text-sm text-[#15803D] group-hover:underline uppercase mt-auto">
                  READ &rarr;
                </span>
              </div>
            </Link>
          ))}
        </div>
      </section>


      {/* ── CONTACT ──────────────────────────────────────────────── */}
      <section data-testid="contact-section" className="py-24 px-4 md:px-8 border-t border-[#E5E5E5]">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            <div>
              <p className="section-tag mb-3">&gt; CONTACT.EXE</p>
              <h2 className="font-pixel text-2xl md:text-4xl text-[#0A0A0A] leading-tight mb-6">
                LET'S BUILD<br />
                <span className="text-[#15803D]">TOGETHER</span>
              </h2>
              <p className="font-terminal text-sm text-[#0A0A0A]/65 leading-relaxed mb-8">
                Ready to upgrade your infrastructure with AI? Whether you need a voice agent, intelligent chat system, or full agentic workflow - we're ready to architect it.
              </p>
              <div className="space-y-4">
                {[
                  { label: "RESPONSE TIME", value: "Within 24 hours" },
                  { label: "ENGAGEMENT TYPE", value: "Consulting / Product license" },
                  { label: "AVAILABILITY", value: "Global - Remote-first" },
                ].map(({ label, value }) => (
                  <div key={label} className="flex gap-4 border-l-2 border-[#15803D] pl-4">
                    <div>
                      <p className="font-arcade text-sm text-[#15803D] uppercase">{label}</p>
                      <p className="font-terminal text-xs text-[#0A0A0A]/70">{value}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <ContactForm />
          </div>
        </div>
      </section>
    </div>
  );
}
